import { useEffect, useId, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  ChevronRightIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@leadsales/design-system/react';
import type { tEmbeddedSignupPath } from 'src/lib/facebook/embeddedSignupLogin';
import type { tConnectionFailedAdvisorCtaKind } from 'src/components/WhatsAppConnect/ConnectionFailedError/ConnectionFailedError.types';
import {
  WIZARD_ASIDE_TITLE_CLASS_NAME,
  WIZARD_BODY_CLASS_NAME,
  WIZARD_ERROR_CATALOG,
  WIZARD_ICON_CLASS_NAME,
  WIZARD_PATH_CARDS,
  WIZARD_TRANSLATION_NAMESPACE,
} from './WhatsAppConnectionWizard.constants';
import {
  findWizardErrorCatalogEntry,
  type tConnectionMoment,
} from './WhatsAppConnectionWizard.functions';
import type { tWizardErrorCatalogId } from './WhatsAppConnectionWizard.types';
import WhatsAppConnectionWizardErrorSteps from './WhatsAppConnectionWizardErrorSteps.component';

/** The moments whose aside is about Meta's errors. */
export type tWizardErrorAsideMoment = Extract<
  tConnectionMoment,
  {
    kind:
      | 'meta_error_window_open'
      | 'meta_error_window_closed'
      | 'closed_before_finishing';
  }
>;

export type tWhatsAppConnectionWizardErrorAsideProps = {
  moment: tWizardErrorAsideMoment;
  /** Null while no answer is chosen; "Tu conexión" then names no path. */
  selectedPath: tEmbeddedSignupPath | null;
  onAdvisorCtaClick: (kind: tConnectionFailedAdvisorCtaKind) => void;
};

/** The control that takes focus after the aside changes view. */
type tAsideFocusTarget = 'otherErrorLink' | 'back' | tWizardErrorCatalogId;

/**
 * The aside after an error report or a close of Meta's window. A known error
 * is explained in the main pane, so the aside shows the connection and a link
 * to the other errors; otherwise it shows the error list. The list is one
 * card of row buttons, and a row opens its entry in place of the list.
 *
 * @param {tWhatsAppConnectionWizardErrorAsideProps} props - The moment, the
 * chosen path and the advisor click report.
 * @returns {JSX.Element} The aside content.
 */
export default function WhatsAppConnectionWizardErrorAside(
  props: tWhatsAppConnectionWizardErrorAsideProps,
) {
  const { moment, selectedPath, onAdvisorCtaClick } = props;
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const listTitleId = useId();
  const [isListOpenedFromLink, setIsListOpenedFromLink] = useState(false);
  const [openEntryId, setOpenEntryId] = useState<tWizardErrorCatalogId | null>(
    null,
  );
  const focusTargetRef = useRef<tAsideFocusTarget | null>(null);
  const focusableByTarget = useRef(
    new Map<tAsideFocusTarget, HTMLButtonElement>(),
  );

  // The control that was clicked leaves the aside with the view it belonged
  // to, so focus moves to the control that takes the person back.
  useEffect(() => {
    const target = focusTargetRef.current;
    if (!target) return;
    focusTargetRef.current = null;
    focusableByTarget.current.get(target)?.focus();
  }, [isListOpenedFromLink, openEntryId]);

  function registerFocusable(target: tAsideFocusTarget) {
    return (element: HTMLButtonElement | null) => {
      if (element) focusableByTarget.current.set(target, element);
      else focusableByTarget.current.delete(target);
    };
  }

  function changeView(
    focusTarget: tAsideFocusTarget,
    view: {
      isListOpenedFromLink: boolean;
      openEntryId: tWizardErrorCatalogId | null;
    },
  ) {
    focusTargetRef.current = focusTarget;
    setIsListOpenedFromLink(view.isListOpenedFromLink);
    setOpenEntryId(view.openEntryId);
  }

  const isMetaWindowOpen = moment.kind === 'meta_error_window_open';
  const isReport = moment.kind !== 'closed_before_finishing';
  const reportedEntry =
    moment.kind === 'closed_before_finishing'
      ? undefined
      : findWizardErrorCatalogEntry(moment.errorReport.code);

  function renderErrorEntry(entryId: tWizardErrorCatalogId) {
    const entry = WIZARD_ERROR_CATALOG.find(
      (catalogEntry) => catalogEntry.id === entryId,
    );
    if (!entry) return null;
    return (
      <div className="flex flex-col gap-[var(--ls-spacing-4)]">
        <div>
          <Button
            ref={registerFocusable('back')}
            color="ghost"
            size="sm"
            type="button"
            onClick={() =>
              changeView(entryId, { isListOpenedFromLink, openEntryId: null })
            }
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            {t('wizard.asideErrors.allErrors')}
          </Button>
        </div>
        <h3 className={WIZARD_ASIDE_TITLE_CLASS_NAME}>
          {t(`wizard.errorCatalog.${entry.id}.title`)}
        </h3>
        <p className={WIZARD_BODY_CLASS_NAME}>
          {t(`wizard.errorCatalog.${entry.id}.cause`)}
        </p>
        <WhatsAppConnectionWizardErrorSteps
          entry={entry}
          isMetaWindowOpen={isMetaWindowOpen}
          showsMetaLink
          // Next to the steps of an error only an advisor can solve, the
          // advisor is that error's fix.
          advisorVariant={entry.whoActs === 'advisor' ? 'secondary' : undefined}
          onAdvisorCtaClick={onAdvisorCtaClick}
        />
      </div>
    );
  }

  function renderErrorList() {
    if (openEntryId) return renderErrorEntry(openEntryId);
    // Opened from a known error, the list leaves that error out.
    const entries = WIZARD_ERROR_CATALOG.filter(
      (entry) => entry.id !== reportedEntry?.id,
    );
    return (
      <div className="flex flex-col gap-[var(--ls-spacing-4)]">
        {isListOpenedFromLink && (
          <div>
            <Button
              ref={registerFocusable('back')}
              color="ghost"
              size="sm"
              type="button"
              onClick={() =>
                changeView('otherErrorLink', {
                  isListOpenedFromLink: false,
                  openEntryId: null,
                })
              }
            >
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
              {t('wizard.asideErrors.back')}
            </Button>
          </div>
        )}
        <h3 id={listTitleId} className={WIZARD_ASIDE_TITLE_CLASS_NAME}>
          {isReport
            ? t('wizard.asideErrors.listTitleOther')
            : t('wizard.asideErrors.listTitleNone')}
        </h3>
        <p className="text-[length:var(--ls-body)] text-[var(--ls-text-neutral-light)]">
          {isMetaWindowOpen
            ? t('wizard.asideErrors.listHintWindowOpen')
            : t('wizard.asideErrors.listHintWindowClosed')}
        </p>
        {/* One card whose rows are split by lines: the DS has no clickable
            list row yet. */}
        <ul
          aria-labelledby={listTitleId}
          className="flex flex-col divide-y divide-[var(--ls-border-neutral-lighter)] overflow-hidden rounded-[var(--ls-radius-lg)] border border-[var(--ls-border-neutral-light)] bg-[var(--ls-surface-elevation-01)]"
        >
          {entries.map((entry) => (
            <li key={entry.id}>
              <button
                ref={registerFocusable(entry.id)}
                type="button"
                onClick={() =>
                  changeView('back', {
                    isListOpenedFromLink,
                    openEntryId: entry.id,
                  })
                }
                className="flex w-full items-center gap-[var(--ls-spacing-3)] px-[var(--ls-spacing-4)] py-[var(--ls-spacing-3)] text-left hover:bg-[var(--ls-surface-elevation-02)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ls-focus-ring-color)]"
              >
                <span className="min-w-0 flex-1 text-[length:var(--ls-body)] font-medium text-[var(--ls-text-neutral-default)]">
                  {t(`wizard.errorCatalog.${entry.id}.title`)}
                </span>
                <ChevronRightIcon
                  className="h-4 w-4 shrink-0 text-[var(--ls-text-neutral-light)]"
                  aria-hidden="true"
                />
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // With Meta's window pinned over the main pane, the aside is what stays in
  // view: it names the error and the one thing to do first.
  if (isMetaWindowOpen) {
    return (
      <div className="flex flex-col gap-[var(--ls-spacing-8)]">
        <div className="flex flex-col gap-[var(--ls-spacing-2)]">
          <h3 className={WIZARD_ASIDE_TITLE_CLASS_NAME}>
            {reportedEntry
              ? t(`wizard.errorCatalog.${reportedEntry.id}.title`)
              : t('wizard.reportedError.unknownTitle')}
          </h3>
          <p className={WIZARD_BODY_CLASS_NAME}>
            {t('wizard.reportedError.asideWindowOpen')}
          </p>
        </div>
        {!reportedEntry && renderErrorList()}
      </div>
    );
  }

  if (!reportedEntry || isListOpenedFromLink || openEntryId) {
    return renderErrorList();
  }

  const pathCard = WIZARD_PATH_CARDS.find((card) => card.path === selectedPath);
  return (
    <div className="flex flex-col gap-[var(--ls-spacing-8)]">
      <section className="flex flex-col gap-[var(--ls-spacing-4)]">
        <h3 className={WIZARD_ASIDE_TITLE_CLASS_NAME}>
          {t('wizard.asideConnection.title')}
        </h3>
        {pathCard && (
          <div className="flex gap-[var(--ls-spacing-2)]">
            <DevicePhoneMobileIcon
              className={WIZARD_ICON_CLASS_NAME}
              aria-hidden="true"
            />
            <div className="flex flex-col gap-[var(--ls-spacing-1)]">
              <p className={WIZARD_BODY_CLASS_NAME}>
                <Trans
                  t={t}
                  i18nKey="wizard.asideConnection.chosenPath"
                  values={{ path: t(pathCard.nameKey) }}
                  components={{ strong: <strong className="font-medium" /> }}
                />
              </p>
              <p className="text-[length:var(--ls-body)] text-[var(--ls-text-neutral-light)]">
                {t(pathCard.chosenBlurbKey)}
              </p>
            </div>
          </div>
        )}
      </section>
      <div>
        <button
          ref={registerFocusable('otherErrorLink')}
          type="button"
          className="ls-link"
          onClick={() =>
            changeView('back', {
              isListOpenedFromLink: true,
              openEntryId: null,
            })
          }
        >
          {t('wizard.asideErrors.otherErrorLink')}
        </button>
      </div>
    </div>
  );
}
